/**
 * Shared procedural AMHS equipment meshes for 3D views.
 * Used by SchematicElement3D (live/DXF layouts) and FactoryElement3D (detailed mode).
 */
import React, { useMemo } from 'react';
import { FACTORY_3D_PALETTE } from '../../utils/factory3dStyles';

type MeshProps = { w: number; h: number; d: number; opacity: number };

type BufferPalette = {
  body: string;
  panel: string;
  base: string;
};

/** Side track buffer / overhead hoist buffer station. */
export const BufferStationMesh: React.FC<
  MeshProps & { palette: BufferPalette; variant?: 'stb' | 'ohb' }
> = ({ w, h, d, palette, opacity, variant = 'stb' }) => {
  const slotCount = variant === 'stb' ? 4 : 3;
  const slots = useMemo(() => {
    const items: React.ReactNode[] = [];
    for (let i = 0; i < slotCount; i++) {
      const t = (i + 1) / (slotCount + 1);
      items.push(
        <mesh key={`slot-${i}`} position={[w / 2, d * (0.28 + t * 0.42), h * 0.92]}>
          <boxGeometry args={[w * 0.72, d * 0.06, h * 0.04]} />
          <meshStandardMaterial
            color={palette.base}
            emissive={palette.panel}
            emissiveIntensity={0.08}
            metalness={0.5}
            roughness={0.35}
          />
        </mesh>
      );
    }
    return items;
  }, [w, h, d, slotCount, palette.panel, palette.base]);

  return (
    <>
      <mesh position={[w / 2, d * 0.1, h / 2]} castShadow receiveShadow>
        <boxGeometry args={[w, d * 0.2, h]} />
        <meshStandardMaterial color={palette.base} metalness={0.45} roughness={0.48} />
      </mesh>
      <mesh position={[w / 2, d * 0.55, h / 2]} castShadow receiveShadow>
        <boxGeometry args={[w * 0.88, d * 0.58, h * 0.72]} />
        <meshStandardMaterial
          color={palette.body}
          metalness={0.48}
          roughness={0.38}
          transparent
          opacity={opacity}
        />
      </mesh>
      {/* Status panel */}
      <mesh position={[w * 0.88, d * 0.58, h / 2]}>
        <boxGeometry args={[w * 0.04, d * 0.32, h * 0.38]} />
        <meshStandardMaterial
          color={palette.panel}
          emissive={palette.panel}
          emissiveIntensity={0.22}
          metalness={0.2}
          roughness={0.4}
        />
      </mesh>
      {/* Top rail for OHB */}
      {variant === 'ohb' && (
        <mesh position={[w / 2, d * 0.92, h / 2]} castShadow>
          <boxGeometry args={[w * 0.75, d * 0.06, h * 0.08]} />
          <meshStandardMaterial color={palette.panel} metalness={0.75} roughness={0.25} />
        </mesh>
      )}
      {slots}
    </>
  );
};

/** Vertical transfer lifter module. */
export const LifterMesh: React.FC<MeshProps> = ({ w, h, d, opacity }) => {
  const frame = FACTORY_3D_PALETTE.lifter;
  const postW = w * 0.08;
  const posts: [number, number][] = [
    [postW / 2, postW / 2],
    [w - postW / 2, postW / 2],
    [postW / 2, h - postW / 2],
    [w - postW / 2, h - postW / 2],
  ];

  return (
    <>
      {posts.map(([px, pz], i) => (
        <mesh key={`post-${i}`} position={[px, d / 2, pz]} castShadow receiveShadow>
          <boxGeometry args={[postW, d, postW]} />
          <meshStandardMaterial
            color={frame.frame}
            metalness={0.65}
            roughness={0.32}
            transparent
            opacity={opacity}
          />
        </mesh>
      ))}
      {/* Shaft panels */}
      <mesh position={[w / 2, d / 2, h * 0.08]}>
        <boxGeometry args={[w * 0.78, d * 0.92, h * 0.04]} />
        <meshStandardMaterial
          color={frame.door}
          metalness={0.55}
          roughness={0.35}
          transparent
          opacity={opacity * 0.85}
        />
      </mesh>
      <mesh position={[w / 2, d / 2, h * 0.92]}>
        <boxGeometry args={[w * 0.78, d * 0.92, h * 0.04]} />
        <meshStandardMaterial
          color={frame.door}
          metalness={0.55}
          roughness={0.35}
          transparent
          opacity={opacity * 0.85}
        />
      </mesh>
      {/* Lift platform */}
      <mesh position={[w / 2, d * 0.18, h / 2]} castShadow>
        <boxGeometry args={[w * 0.7, d * 0.06, h * 0.7]} />
        <meshStandardMaterial
          color={frame.platform}
          emissive={frame.platform}
          emissiveIntensity={0.15}
          metalness={0.35}
          roughness={0.45}
        />
      </mesh>
      {/* Door window */}
      <mesh position={[w / 2, d * 0.55, h * 0.06]}>
        <boxGeometry args={[w * 0.45, d * 0.5, h * 0.025]} />
        <meshStandardMaterial
          color="#31C8C8"
          emissive="#31C8C8"
          emissiveIntensity={0.35}
          metalness={0.1}
          roughness={0.15}
          transparent
          opacity={0.75}
        />
      </mesh>
      {/* Motor housing */}
      <mesh position={[w / 2, d * 0.94, h / 2]} castShadow>
        <boxGeometry args={[w * 0.55, d * 0.1, h * 0.45]} />
        <meshStandardMaterial color={frame.body} metalness={0.5} roughness={0.4} />
      </mesh>
    </>
  );
};

/** Dispatch to the correct equipment mesh by layout element type. */
export const EquipmentFloorMesh: React.FC<
  MeshProps & { type: 'stb' | 'ohb' | 'lifter' }
> = ({ type, w, h, d, opacity }) => {
  switch (type) {
    case 'stb':
      return (
        <BufferStationMesh
          w={w}
          h={h}
          d={d}
          opacity={opacity}
          palette={FACTORY_3D_PALETTE.stb}
          variant="stb"
        />
      );
    case 'ohb':
      return (
        <BufferStationMesh
          w={w}
          h={h}
          d={d}
          opacity={opacity}
          palette={FACTORY_3D_PALETTE.ohb}
          variant="ohb"
        />
      );
    case 'lifter':
      return <LifterMesh w={w} h={h} d={d} opacity={opacity} />;
    default:
      return null;
  }
};
